import {Box, Checkbox, Typography} from '@mui/material';
import {GridRenderCellParams} from '@mui/x-data-grid';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  useAddAssignedReviewers,
  useCallBulkActionsPopupNonBg,
  useCallBulkActionsLikePopupNonbg,
  useCompleteDocs,
  useDoActionNonBg,
  useGetDistributedDocs,
  useGetUnAssignedUsers,
  useIncompleteDocs,
  useRemoveDocuments,
  useUnAssignUser,
} from '../../../../../hooks/assignments/useAssignments';
import {RootState} from '../../../../../redux/store';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import {
  createDocumentViewerListRequestPayloadFromAssignments,
  manage_reviewers,
} from '../../../AssignmentsUtils';
import './ManageReviewers.scss';
import {ReactComponent as PlusIcon} from '../../../../../assets/images/plus-icon.svg';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import {ReactComponent as UnCheckedIcon} from '../../../../../assets/images/icon-unchecked.svg';
import {ReactComponent as CheckedIcon} from '../../../../../assets/images/icon-checked.svg';
import {
  IAddAssignedReviewers,
  IBulkActionLikePopupRequest,
  INotAssignedUsers,
  IBulkActionPopupNonBgResponse,
} from '../../../../../api/types/assignment.type';
import {
  ActionTypes,
  RoleId,
  SourceTypeCode,
  ToastType,
} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {CustomMenuItem} from '../../../../common/arr-menu/ArrMenu';
import ActionMenuWrapper from '../../../../action-menu/action-menu-wrapper/ActionMenuWrapper';
import {DocActionMenuItem} from '../../../../../shared/interfaces';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {
  clearDocViewState,
  createDocumentListRequestPayload,
} from '../../../../document-viewer/docViewerUtils';
import {
  storeDocumentListMasterId,
  storeSourceId,
  storeSourceTypeCode,
} from '../../../../../redux/slice/docViewSlice';
import {
  useDocumentList,
  useDocumentViewerListId,
} from '../../../../../hooks/document/useDocument';
import {DocumentViewerListIdRequest} from '../../../../../api/types/document.type';
import ArrDocMaxLimitWarning from '../../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {useNavigate} from 'react-router-dom';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../../redux/slice/docListSlice';
import {setBackSourceTabIndex} from '../../../../../redux/slice/assignmentSlice';
import BulkFolderUnFolder from '../../../../action-menu/bulk-folder-unfolder/BulkFolderUnFolder';

import {ActionSelection} from '../../../../doc-explorer/ViewDocExplorer';
import BulkTagUntag from '../../../../action-menu/bulk-tag-untag/BulkTagUntag';
import ActionLikeDocuments, {
  IActionListItem,
} from '../../../../action-menu/action-like-documents/ActionLikeDocuments';
import BulkTaggingWarning from '../../../../action-menu/bulk-tagging-warning/BulkTaggingWarning';
import {isEmpty} from 'lodash';

export const MANAGE_REVIEWERS_ACTION_MENU: DocActionMenuItem[] = [
  {
    id: 'RemoveDocs',
    name: 'Remove Docs',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'FolderDocsReviewer',
    name: 'Folder Docs',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'BulkTagReviewer',
    name: 'Bulk Tag',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'View',
    name: 'View',
    Roles: [RoleId.ReviewManager, RoleId.Reviewer],
    subItems: [
      {
        id: 'ViewInDocView',
        name: 'ViewInDocView',
        Roles: [RoleId.ReviewManager],
      },
      {
        id: 'ViewInDocViewInNewTab',
        name: 'ViewInDocViewInNewTab',
        Roles: [RoleId.ReviewManager],
      },
      {
        id: 'ViewInDocList',
        name: 'ViewInDocList',
        Roles: [RoleId.ReviewManager],
      },
    ],
  },
  {
    id: 'CompleteDocs',
    name: 'Complete Docs',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'UncompleteDocs',
    name: 'Uncomplete Docs',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'UnassignReviewer',
    name: 'Unassign',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'Redistribute',
    name: 'Redistribute',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
];

const initialActionList: IActionListItem[] = [
  {
    id: 'family_members',
    label: t(`common.actionMenu.familyMembers`),
    isLoading: false,
    count: 0,
    selected: false,
  },
  {
    id: 'email_docs',
    label: t(`common.actionMenu.emailThread`),
    isLoading: false,
    count: 0,
    selected: false,
  },
  {
    id: 'near_duplicates',
    label: t(`common.actionMenu.nearDuplicates`),
    isLoading: false,
    count: 0,
    selected: false,
  },
];

const ManageReviewers = () => {
  const [selectedRowId, setSelectedRowId] = useState<number>();
  const [isAddRevierwerMenuOpen, setIsAddRevierwerMenuOpen] =
    useState<boolean>(false);
  const [unAssignedUsers, setUnAssignedUsers] = useState<any>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [reviewerName, setReviewerName] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [actionMenuSelected, setActionMenuSelected] = useState<string>('');
  const [isBulkTagAction, setIsBulkTagAction] = useState(false);
  const [selectedNewExistingTag, setSelectedNewExistingTag] = useState<
    ActionSelection | null | undefined
  >();
  const [masterDocListId, setMasterDocListId] = useState<number>(0);
  const [actionList] = useState(initialActionList); // Folder Docs Action states
  const [isFolderDocsAction, setIsFolderDocsAction] = useState<boolean>(false);
  const [isActionLikeModal, setIsActionLikeModal] = useState<boolean | string>(
    false
  );
  const [totalDocsCount, setTotalDocsCount] = useState<number>(0); // Add this state
  const [selectedNewExistingFolder, setSelectedNewExistingFolder] =
    useState<ActionSelection | null>(null);
  const [bulkSelectedAction, setBulkSelectedAction] = useState<string>('');
  const [selectedTreeIds, setSelectedTreeIds] = useState<string[]>([]);
  const [analyticsIndicatorStatus, setAnalyticsIndicatorStatus] =
    useState<number>(0);
  const [updatedLikeActionList, setUpdatedLikeActionList] = useState<
    IActionListItem[]
  >([]);

  const {selectedData, assignmentList} = useSelector(
    (state: RootState) => state.assignment
  );
  const {assignmentId} = selectedData;

  const [showDocLimitDialog, setShowDocLimitDialog] = useState<boolean>(false);
  const [docLimitSourceTypeCode, setDocLimitSourceTypeCode] =
    useState<SourceTypeCode>();
  const [nonBgStandardCount, setNonBgStandardCount] = useState<number>(0);

  const [isBulkWarning, setIsBulkWarning] = useState<boolean>(false);

  const [actionLikeChangeList, setActionLikeChangeList] =
    useState<IActionListItem[]>();

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const {
    data: distributeDocData,
    refetch: refetchGetDistributedDocs,
    isLoading: isLoadingGetDistributedDocs,
  } = useGetDistributedDocs(assignmentId);
  const {
    data: unAssignedUsersData,
    isLoading: isLoadingGetUnAssignedUsers,
    refetch: refetchUnAssignedUsers,
  } = useGetUnAssignedUsers(assignmentId);
  const {
    mutate: addAssignedReviewersMutate,
    data: addAssignedReviewersRes,
    error: errorAddAssignedReviewers,
    isPending: isLoadingAddAssignedReviewers,
  } = useAddAssignedReviewers();
  const {
    mutate: unAssignUserMutate,
    reset: resetUnAssignUser,
    isPending: isLoadingUnAssignUser,
  } = useUnAssignUser();

  const {
    mutate: removeDocumentsMutate,
    reset: resetRemoveDocuments,
    isPending: isLoadingRemoveDocuments,
  } = useRemoveDocuments();

  const {mutate: completeDocs} = useCompleteDocs();
  const {mutate: incompleteDocs} = useIncompleteDocs();

  const {mutate: fetchDocumentViewerListId, isPending: isDocViewLoading} =
    useDocumentViewerListId();

  const {mutate: fetchDocumentList, isPending: isDocListLoading} =
    useDocumentList();

  // Bulk Actions API hooks for Folder Docs
  const {
    mutate: callBulkActionsPopupNonbg,
    isPending: isBulkActionsPopupLoading,
  } = useCallBulkActionsPopupNonBg();

  const {
    mutate: callBulkActionsLikePopupNonbg,
    isPending: isLoadingBulkActionsLikePopup,
  } = useCallBulkActionsLikePopupNonbg();

  const {mutate: doActionNonBg, isPending: isDoActionLoading} =
    useDoActionNonBg();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (unAssignedUsersData?.notAssignedUsers) {
      setUnAssignedUsers(
        unAssignedUsersData?.notAssignedUsers?.map((item) => ({
          ...item,
          isChecked: false,
        })) ?? []
      );
    }
  }, [unAssignedUsersData]);

  useEffect(() => {
    setReviewerName(
      assignmentList.tableData.find(
        (item) => item.assignmentId === assignmentId
      )?.assignmentName ?? ''
    );
  }, [assignmentList]);

  useEffect(() => {
    if (addAssignedReviewersRes?.message) {
      refetchGetDistributedDocs();
      refetchUnAssignedUsers();
      setIsAddRevierwerMenuOpen(false);
      triggerToast(addAssignedReviewersRes?.message, ToastType.Success);
    } else if (errorAddAssignedReviewers) {
      triggerToast(errorAddAssignedReviewers.message, ToastType.Success);
    }
  }, [addAssignedReviewersRes, errorAddAssignedReviewers]);

  const documentViewerListPayload = useCallback(
    (totalDocumentToView: number): DocumentViewerListIdRequest =>
      createDocumentViewerListRequestPayloadFromAssignments({
        sourceID: assignmentId ?? 0,
        totalDocumentToView,
        objSourceOverrides: {reviewerID: selectedRowId ?? 0},
      }),
    [assignmentId, selectedRowId]
  );

  const documentListPayload = useCallback(
    (totalDocumentToView: number) =>
      createDocumentListRequestPayload({
        sourceId: assignmentId ?? 0,
        sourceTypeCode: SourceTypeCode.ASSIGNMENT,
        docViewSourceTypeCode: SourceTypeCode.ASSIGNMENT,
        docViewSessionOverrides: {
          assignmentSortDetails: [
            {
              sourceID: assignmentId ?? 0,
              columnManual: '',
              columnOptimized: '',
              columnIDsOptimized: '',
              columnOrderOptimized: '',
              columnIDsManual: '',
              columnOrderManual: '',
              showCompletedOptimized: true,
              showCompletedManual: false,
              configuredShowCompletedDoc: false,
              columnDisplayManual: '',
              columnDataTypeOptimized: [],
              columnDataTypeManual: [],
            },
          ],
        },
        sourceInfoOverrides: {
          totalDocumentToView,
          destinationContextTypeCode: 'DOCLIST',
          reviewerID: selectedRowId ?? 0,
        },
      }),
    [assignmentId, selectedRowId]
  );

  const renderActionsCell = useCallback(
    (params: GridRenderCellParams) => {
      const row = params.row;
      const rowReviewerId = row?.reviewerId;

      if (!rowReviewerId) {
        return null;
      }

      const handleRadioChange = (val: string) => {
        const numVal = Number(val);
        if (!Number.isNaN(numVal)) {
          setSelectedRowId(numVal);
        }
      };

      return (
        <Box className="assignment-grid-action">
          <ArrRadio
            dataTestId={`assignment-grid-radio-${rowReviewerId}`}
            id={`assignment-grid-radio-${rowReviewerId}`}
            name="assignment-radio"
            value={
              selectedRowId === rowReviewerId ? rowReviewerId.toString() : ''
            }
            onChange={handleRadioChange}
            data={[
              {
                value: rowReviewerId.toString(),
                label: '',
              },
            ]}
            size="xs"
            className="row-radio"
          />
        </Box>
      );
    },
    [selectedRowId]
  );

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const onChangeCheckbox = (user: INotAssignedUsers) => {
    const localUnAssignedUser = unAssignedUsers?.map(
      (item: INotAssignedUsers) => ({
        ...item,
        isChecked:
          user.reviewerId === item.reviewerId
            ? !item.isChecked
            : item.isChecked,
      })
    );
    setUnAssignedUsers(localUnAssignedUser);
  };

  const handleAddReviewers = () => {
    const payload: IAddAssignedReviewers = {
      assignmentId: assignmentId,
      reviewers: unAssignedUsers
        ?.filter((item: INotAssignedUsers) => item.isChecked)
        ?.map((item: INotAssignedUsers) => ({
          reviewerId: item.reviewerId,
          emailId: item.emailId,
        })),
    };
    addAssignedReviewersMutate(payload);
  };

  const handleOnCloseDrawer = () => {
    setIsAddRevierwerMenuOpen(false);
    const localUnAssignedUser = unAssignedUsers?.map(
      (item: INotAssignedUsers) => ({
        ...item,
        isChecked: false,
      })
    );
    setUnAssignedUsers(localUnAssignedUser);
  };

  const isAddBtnEnabled = useMemo(
    () => unAssignedUsers.some((item: any) => item.isChecked),
    [unAssignedUsers]
  );

  // Get selected reviewer data
  const selectedReviewer = useMemo(() => {
    if (!selectedRowId || !distributeDocData?.assignedUsers) return null;
    return distributeDocData.assignedUsers.find(
      (user) => user.reviewerId === selectedRowId
    );
  }, [selectedRowId, distributeDocData]);

  // Check if reviewer has documents (1 or more docs assigned)
  const hasDocuments = useMemo(() => {
    return (selectedReviewer?.assigned ?? 0) > 0;
  }, [selectedReviewer]);

  // Check if reviewer has incomplete documents (1 or more docs left to do)
  const hasIncompleteDocs = useMemo(() => {
    return (selectedReviewer?.toDo ?? 0) > 0;
  }, [selectedReviewer]);

  // Check if reviewer has completed documents (1 or more docs completed)
  const hasCompletedDocs = useMemo(() => {
    return (selectedReviewer?.completed ?? 0) > 0;
  }, [selectedReviewer]);

  // ====================================
  // FOLDER DOCS ACTION HANDLERS (Local)
  // ====================================

  // Handle Folder Docs Action - Initialize the flow
  const handleFolderDocsAction = () => {
    if (!selectedRowId || !assignmentId) return;

    const payload = {
      sourceType: 'ASSIGNMENTREVIEWER',
      actionType: 'FOLDERNonBG',
      elementID: 'divBulkAction',
      selectedHitsDetails: [],
      sourceDetails: JSON.stringify({
        LstBulkActionContext: [
          {
            ID: selectedReviewer?.reviewerId,
          },
        ],
        ContextContainerID: assignmentId,
      }),
      sourceTypeContext: '',
      documentCount: '',
    };

    callBulkActionsPopupNonbg(payload, {
      onSuccess: (response) => {
        setTotalDocsCount(response?.documentCounts?.standardCount ?? 0);
        setIsFolderDocsAction(true);
      },
      onError: (error: any) => {
        triggerToast(
          error?.message ||
            t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError'),
          ToastType.Error
        );
      },
    });
  };

  // Handle continue from Bulk Folder flyout
  const handleBulkFolderContinue = async (
    action: string,
    docCount?: number,
    newFolderId?: number
  ) => {
    setBulkSelectedAction(action);

    let treeIds: number[] = [];

    if (selectedNewExistingFolder) {
      const ids = Array.isArray(selectedNewExistingFolder.ids)
        ? selectedNewExistingFolder.ids
        : [selectedNewExistingFolder.ids];

      treeIds = ids.map(Number);
    }

    if (newFolderId) {
      treeIds.push(newFolderId);
      setSelectedTreeIds([...treeIds.map(String)]);
    }

    if (action === 'folderDocs') {
      const likePopupPayload: IBulkActionLikePopupRequest = {
        sourceType: 'ASSIGNMENTREVIEWER',
        actionType: 'FOLDER',
        elementID: 'divBulkAction',
        sourceTypeContext: '',
        sourceDetails: JSON.stringify({
          LstBulkActionContext: [
            {
              ID: selectedReviewer?.reviewerId,
            },
          ],
          ContextContainerID: assignmentId,
        }),
        destinationTypeContext: '',
        selectedHitsDetails: [],
        documentCount: '0',
        masterDocListID: masterDocListId,
        isAdd: true,
        documentListMasterID: 0,
        docAssign: totalDocsCount,
        selectedDetinationIds: JSON.stringify({
          BulkActionTreeIDs: treeIds,
        }),
      };

      callBulkActionsLikePopupNonbg(likePopupPayload, {
        onSuccess: (response) => {
          setAnalyticsIndicatorStatus(response.analyticsIndicatorStatus ?? 0);
          setIsActionLikeModal(true);
        },
        onError: (error: any) => {
          triggerToast(
            error?.message ||
              t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError'),
            ToastType.Error
          );
        },
      });
    } else if (action === 'unfolderDocs') {
      executeFolderAction(action, docCount);
    }
  };

  // Execute the folder/unfolder action
  const executeFolderAction = (action: string, _docCount?: number) => {
    const getIncludeFlags = () => {
      const includeFamilyMember = updatedLikeActionList.some(
        (item) => item.id === 'family_members' && item.selected
      );
      const includeEmailThreads = updatedLikeActionList.some(
        (item) => item.id === 'email_docs' && item.selected
      );
      const includeNearDuplicates = updatedLikeActionList.some(
        (item) => item.id === 'near_duplicates' && item.selected
      );

      return {
        IncludeFamilyMember: includeFamilyMember,
        IncludeEmailThreads: includeEmailThreads,
        IncludeNearDuplicates: includeNearDuplicates,
      };
    };

    const includeFlags = getIncludeFlags();
    const isAdd = action === 'folderDocs';
    const selectedListObject = {
      LstBulkActionContext: [
        {
          ID: selectedReviewer?.reviewerId,
        },
      ],
      ContextContainerID: assignmentId,
    };
    const payload = {
      sourceType: 'ASSIGNMENTREVIEWER',
      actionType: 'FOLDER',
      elementId: 'divBulkAction',
      SelectedList: selectedListObject,
      destinationList: {
        BulkActionTreeIDs: selectedTreeIds.map(Number),
        IsAdd: isAdd,
        SampleMethod: 'STANDARD',
        NumberToAssign: _docCount,
        ...includeFlags,
      },
    };

    doActionNonBg(payload, {
      onSuccess: () => {
        setIsActionLikeModal(false);
        setIsFolderDocsAction(false);
        resetFolderActionStates();

        refetchGetDistributedDocs();
        triggerToast(
          t('docExplorer.actionMenu.bulkTaggingWarning.doActionSuccess'),
          ToastType.Success
        );
      },
      onError: (error: any) => {
        triggerToast(
          error?.message ||
            t('docExplorer.actionMenu.bulkTaggingWarning.doActionError'),
          ToastType.Error
        );
      },
    });
  };

  const handleActionLikeContinue = (docCount?: number) => {
    executeFolderAction(bulkSelectedAction, docCount);
  };

  const handleActionLikeCancel = () => {
    setIsActionLikeModal(false);

    if (selectedNewExistingFolder?.type === 'new') {
      setIsFolderDocsAction(false);
      resetFolderActionStates();
    }
  };

  const handleActionLikeClose = () => {
    setIsActionLikeModal(false);

    if (selectedNewExistingFolder?.type === 'new') {
      setIsFolderDocsAction(false);
      resetFolderActionStates();
    }
  };

  const onActionLikeChange = (
    index: number,
    status: boolean,
    updatedActionItems: IActionListItem[]
  ) => {
    setUpdatedLikeActionList(updatedActionItems);
  };

  // Reset folder action states
  const resetFolderActionStates = () => {
    setMasterDocListId(0);
    setMasterDocListId(0);
    setSelectedNewExistingFolder(null);
    setBulkSelectedAction('');
    setSelectedTreeIds([]);
    setUpdatedLikeActionList([]);
    setAnalyticsIndicatorStatus(0);
  };

  // ====================================
  // ACTION MENU HANDLER
  // ====================================

  const handleActionClick = (item: CustomMenuItem) => {
    // Redistribute is handled internally by ActionMenuWrapper via getRedistributeData
    if (item.id === 'Redistribute') {
      return;
    }

    setActionMenuSelected(item.id);

    if (item.id === 'UnassignReviewer') {
      setShowModal(true);
      setModalTitle(t('assignment.manageReviewers.modalTitle.unAssign'));
    } else if (item.id === 'RemoveDocs') {
      setShowModal(true);
      setModalTitle(t('assignment.manageReviewers.modalTitle.removeDocs'));
    }

    if (item.id === 'CompleteDocs' || item.id === 'UncompleteDocs') {
      if (loginUserData?.baseRoleId !== loginUserData?.roleId) {
        return triggerToast(
          t('assignment.manageReviewers.impersonatedError'),
          ToastType.Error
        );
      }
      setModalTitle(
        t(
          `assignment.manageReviewers.modalTitle.${item.id === 'CompleteDocs' ? 'completeAllDocs' : 'uncompleteAllDocs'}`
        )
      );
      setShowModal(true);
    } else if (item.id === 'ViewInDocView') {
      setModalTitle(t('assignment.manageReviewers.modalTitle.viewInDocView'));
      setShowModal(true);
    } else if (item.id === 'ViewInDocViewInNewTab') {
      setModalTitle(
        t('assignment.manageReviewers.modalTitle.viewInDocViewInTab')
      );
      setShowModal(true);
    } else if (item.id === 'ViewInDocList') {
      setModalTitle(t('assignment.manageReviewers.modalTitle.viewInDocList'));
      setShowModal(true);
    } else if (item.id === 'BulkTagReviewer') {
      setModalTitle(t('assignment.manageReviewers.modalTitle.bulkTag'));
      const payload = {
        sourceType: SourceTypeCode.ASSIGNMENTREVIEWER,
        actionType: ActionTypes.TAGNonBG,
        elementID: 'divBulkAction',
        selectedHitsDetails: [],
        sourceDetails: JSON.stringify({
          LstBulkActionContext: [{ID: selectedRowId}],
          ContextContainerID: assignmentId,
        }),
      };
      callBulkActionsPopupNonBg(payload);
      setIsBulkTagAction(true);
    } // Handle Folder Docs locally - don't pass to ActionMenuWrapper
    else if (item.id === 'FolderDocsReviewer') {
      handleFolderDocsAction();
      return;
    }
  };

  const {mutate: callBulkActionsPopupNonBg} = useCallBulkActionsPopupNonBg(
    (response: IBulkActionPopupNonBgResponse) => {
      setNonBgStandardCount(response.documentCounts.standardCount);
    },
    (_error) => {
      setToastMessage(
        t('ManageReviewers.actionMenu.bulkTaggingWarning.bulkTagError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const handleUnAssignUser = () => {
    if (!selectedRowId || !assignmentId) return;
    const payload = {
      assignmentId: assignmentId,
      reviewerId: selectedRowId,
    };
    unAssignUserMutate(payload, {
      onSuccess: (data) => {
        triggerToast(data.message, ToastType.Success);
        resetUnAssignUser();
        resetStates();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
        resetUnAssignUser();
      },
    });
  };

  const handleRemoveDocs = () => {
    if (!selectedRowId || !assignmentId) return;
    const payload = `actionType=RemoveDocuments&assignmentId=${assignmentId}&reviewerId=${selectedRowId}`;
    removeDocumentsMutate(payload, {
      onSuccess: () => {
        triggerToast(
          t('assignment.manageReviewers.removeDocsSuccess'),
          ToastType.Success
        );
        resetRemoveDocuments();
        resetStates();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
        resetRemoveDocuments();
      },
    });
  };

  const handleYesClick = () => {
    if (actionMenuSelected === 'UnassignReviewer') {
      handleUnAssignUser();
    } else if (actionMenuSelected === 'RemoveDocs') {
      handleRemoveDocs();
    }
    if (actionMenuSelected === 'CompleteDocs') {
      handleCompleteDocs();
    } else if (actionMenuSelected === 'UncompleteDocs') {
      handleIncompleteDocs();
    } else if (
      actionMenuSelected === 'ViewInDocView' ||
      actionMenuSelected === 'ViewInDocViewInNewTab'
    ) {
      setShowModal(false);
      setDocLimitSourceTypeCode(SourceTypeCode.DOCVIEW);
      setShowDocLimitDialog(true);
    } else if (actionMenuSelected === 'ViewInDocList') {
      setShowModal(false);
      setDocLimitSourceTypeCode(SourceTypeCode.DOCLIST);
      setShowDocLimitDialog(true);
    }
  };

  const checkLoading = () => {
    if (actionMenuSelected === 'UnassignReviewer') {
      return isLoadingUnAssignUser;
    } else if (actionMenuSelected === 'RemoveDocs') {
      return isLoadingRemoveDocuments;
    }
    return false; // Add other conditions for different actions if needed
  };

  const resetStates = () => {
    refetchGetDistributedDocs();
    refetchUnAssignedUsers();
    setShowModal(false);
    setModalTitle('');
    setSelectedRowId(undefined);
    setActionMenuSelected('');
  };

  const filteredActions = useMemo((): CustomMenuItem[] => {
    const mapAndFilter = (items: DocActionMenuItem[]): CustomMenuItem[] =>
      items.map((it) => {
        let isDisabled = false;

        if (!selectedRowId) {
          isDisabled = true;
        } else {
          switch (it.id) {
            case 'UnassignReviewer':
              isDisabled = false;
              break;
            case 'CompleteDocs':
              isDisabled = !hasDocuments || !hasIncompleteDocs;
              break;
            case 'UncompleteDocs':
              isDisabled = !hasDocuments || !hasCompletedDocs;
              break;
            case 'Redistribute':
              isDisabled = !hasIncompleteDocs;
              break;
            case 'RemoveDocs':
              isDisabled = !hasIncompleteDocs;
              break;
            case 'FolderDocsReviewer':
              isDisabled = !hasDocuments;
              break;
            case 'View':
              isDisabled = !hasDocuments;
              break;
            default:
              isDisabled = !hasDocuments;
              break;
          }
        }

        return {
          id: String(it.id),
          label: t(`assignment.manageReviewers.items.${it.id}`),
          disabled: isDisabled,
          submenu: it.subItems ? mapAndFilter(it.subItems) : undefined,
        };
      });
    return mapAndFilter(MANAGE_REVIEWERS_ACTION_MENU);
  }, [selectedRowId, hasDocuments, hasIncompleteDocs, hasCompletedDocs]);

  const handleCompleteDocs = () => {
    const payload = {
      assignmentId,
      reviewerId: selectedRowId,
    };
    completeDocs(payload, {
      onSuccess: (data) => {
        triggerToast(data.message, ToastType.Success);
        setShowModal(false);
        refetchGetDistributedDocs();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };

  const handleIncompleteDocs = () => {
    const payload = {
      assignmentId,
      reviewerId: selectedRowId,
    };
    incompleteDocs(payload, {
      onSuccess: (data) => {
        triggerToast(data.message, ToastType.Success);
        setShowModal(false);
        refetchGetDistributedDocs();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };

  const onContinueDocLimit = useCallback(
    (totalDocumentToView: number) => {
      setShowDocLimitDialog(false);
      switch (docLimitSourceTypeCode) {
        case SourceTypeCode.DOCVIEW: {
          fetchDocumentViewerListId(
            documentViewerListPayload(totalDocumentToView),
            {
              onSuccess: (response) => {
                clearDocViewState();
                const masterId =
                  response?.documentListID ?? response?.defaultMasterID ?? 0;

                switch (actionMenuSelected) {
                  case 'ViewInDocView': {
                    dispatch(storeDocumentListMasterId(String(masterId)));
                    dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
                    dispatch(storeSourceId(assignmentId ?? 0));

                    navigate('/DocumentViewer/DocView');
                    break;
                  }
                  case 'ViewInDocViewInNewTab': {
                    const newWindow = window.open('about:blank', '_blank');
                    if (newWindow) {
                      dispatch(storeDocumentListMasterId(String(masterId)));
                      dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
                      dispatch(storeSourceId(assignmentId ?? 0));

                      newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
                    }
                    break;
                  }
                  default: {
                    break;
                  }
                }
              },
              onError: (error) => {
                triggerToast(error?.message ?? '', ToastType.Error);
              },
            }
          );
          break;
        }
        case SourceTypeCode.DOCLIST: {
          fetchDocumentList(documentListPayload(totalDocumentToView), {
            onSuccess: (response) => {
              const masterIdRaw =
                response.docId ??
                response.doclistId ??
                response.documentListID ??
                response.defaultMasterID ??
                response.docViewSessionObjectsRes?.documentListMasterID ??
                0;
              const masterId = Number(masterIdRaw) || 0;
              clearDocViewState();
              setTimeout(() => {
                dispatch(
                  setDocListBackNavigation({
                    label: t('assignment.edit.editAssignment'),
                    path: `${window.location.pathname}${window.location.search}`,
                  })
                );
                dispatch(setBackSourceTabIndex(1));
                dispatch(setDocListId(Number(masterId)));
                dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
                dispatch(storeSourceId(assignmentId ?? 0));
                navigate('/Document/DocList');
              }, 3000);
            },
            onError: (error) => {
              triggerToast(error?.message ?? '', ToastType.Error);
            },
          });
          break;
        }
        default: {
          break;
        }
      }
    },
    [
      actionMenuSelected,
      assignmentId,
      dispatch,
      docLimitSourceTypeCode,
      documentViewerListPayload,
      fetchDocumentViewerListId,
      navigate,
    ]
  );

  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitDialog(false);
  }, []);

  const getRedistributeData = useCallback(() => {
    return {
      redistributeCount: selectedReviewer?.toDo ?? 0,
      assignmentId: assignmentId ?? 0,
      currentReviewerId: String(selectedRowId ?? ''),
    };
  }, [selectedReviewer, assignmentId, selectedRowId]);

  const handleBulkTagContinue = async (selectedAction: string) => {
    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    if (selectedAction === 'untagDocs') {
      setIsBulkWarning(true);
    } else if (selectedAction === 'tagDocs') {
      callBulkActionsLikePopupApiCall(bulkActionTreeIDs, () => {
        setIsActionLikeModal('BulkTagReviewer');
      });
    }
  };

  const callBulkActionsLikePopupApiCall = (
    bulkActionTreeIDs: string[],
    onSuccessCallback?: () => void
  ) => {
    const bulkTreeIDs = bulkActionTreeIDs.map(Number);
    const likePopupPayload: IBulkActionLikePopupRequest = {
      sourceType: SourceTypeCode.ASSIGNMENTREVIEWER,
      actionType: ActionTypes.TAG,
      elementID: 'divBulkAction',
      sourceTypeContext: '',
      sourceDetails: JSON.stringify({
        LstBulkActionContext: [{ID: selectedRowId}],
        ContextContainerID: assignmentId,
      }),
      destinationTypeContext: '',
      selectedHitsDetails: [],
      documentCount: '0',
      masterDocListID: 0,
      isAdd: true,
      documentListMasterID: 0,
      docAssign: 0,
      selectedDetinationIds: JSON.stringify({
        BulkActionTreeIDs: bulkTreeIDs,
      }),
    };

    callBulkActionsLikePopupNonbg(likePopupPayload);

    if (onSuccessCallback) {
      onSuccessCallback();
    }
  };

  const onActionLikeChangeList = (
    index: number,
    status: boolean,
    updatedActionItems: IActionListItem[]
  ) => {
    setActionLikeChangeList(updatedActionItems);
  };

  const handleActionLike = (_docCount?: number) => {
    if (isActionLikeModal === 'BulkTagReviewer') {
      setIsBulkWarning(true);
    }
    setIsActionLikeModal(false);
    setAnalyticsIndicatorStatus(0);
  };
  const handleBulkWarning = () => {
    setActionLikeChangeList(undefined);
    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    const doActionNonBgPayload = {
      sourceType: SourceTypeCode.ASSIGNMENTREVIEWER,
      actionType: 'TAG',
      elementId: 'divBulkAction',
      SelectedList: {
        LstBulkActionContext: [{ID: selectedRowId}],
      },
      destinationList: {
        BulkActionTreeIDs: bulkActionTreeIDs,
        IsAdd: false,
        SampleMethod: '',
        NumberToAssign: '0',
      },
      strSelections: null,
    };
    doBulkActionNonBg(doActionNonBgPayload);
  };

  const {mutate: doBulkActionNonBg} = useDoActionNonBg(
    () => {
      setIsBulkWarning(false);
      setIsBulkTagAction(false);
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.doActionSuccess')
      );
      setToastStatus(ToastType.Success);
      setShowToast(true);
    },
    (_error) => {
      setToastMessage(
        t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError')
      );
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  );

  const getSelectedNewExistingTag = (): string[] => {
    const ids = selectedNewExistingTag?.ids;
    if (!isEmpty(ids)) {
      if (typeof ids === 'string') {
        return ids ? [ids] : [];
      }
      return ids || [];
    }
    return [];
  };

  const contentBody = (
    <Box className="add-reviewers-body">
      <Box className="add-reviewers-header">
        <Box className="heading-text-container">
          <Typography
            className="typo-text headline3_size add-reviewers-text"
            data-testid={`manage-reviewers-addreviewers`}
          >
            {t('assignment.manageReviewers.addreviewers')}
          </Typography>
          <InfoIcon className="info-icon" aria-label="Information" />
        </Box>
        <Box className="button-text-container">
          <ArrButton
            variant="secondary"
            size="small"
            dataTestId="action-button-cancel"
            aria-label={t('common.cancel')}
            onClick={handleOnCloseDrawer}
            id="cancel-button"
            name="cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="small"
            dataTestId="add-reviewers-button-primary"
            aria-label={t('common.add')}
            className="add-reviewer-btn"
            onClick={handleAddReviewers}
            id="add-reviewers-confirm-button"
            name="add-reviewers-confirm-button"
            disabled={!isAddBtnEnabled}
          >
            {t('common.add')}
          </ArrButton>
        </Box>
      </Box>
      {unAssignedUsers?.map((item: INotAssignedUsers, index) => (
        <Box className="checkbox-container" key={item.reviewerId}>
          <Checkbox
            data-testid={`manage-reviewers-unassigned-checkbox-${item.reviewerId}`}
            checked={item.isChecked}
            onChange={() => onChangeCheckbox(item)}
            className="checkbox"
            icon={<UnCheckedIcon />}
            checkedIcon={<CheckedIcon />}
            inputProps={{
              'aria-label': `Select ${item.reviewerName}`,
            }}
          />
          <Typography
            className={`typo-text body3_size checkbox-text`}
            noWrap
            data-testid={`manage-reviewers-reviewer-name-${index}`}
          >
            {item.reviewerName}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  if (isLoadingGetDistributedDocs) {
    return <ArrLoadingSpinner />;
  }
  return (
    <Box className="manage-reviewers-container">
      <Box className="manage-reviewers-filter">
        <Typography
          className="typo-text subhead1_size"
          data-testid={`manage-reviewers-typography-text`}
        >
          {`${reviewerName} ${t('assignment.manageReviewers.gridHeading')}`}
        </Typography>
        <Box>
          <ActionMenuWrapper
            actions={filteredActions}
            isDisabled={!selectedRowId}
            size="small"
            dataTestId="action-button-manage-reviewers"
            onActionPick={handleActionClick}
            sourceType={SourceTypeCode.ASSIGNMENT}
            sourceDetails={{
              assignmentId: assignmentId,
              reviewerId: selectedRowId,
            }}
            direction="bottom-left"
            subMenuDirection="left"
            menuClassName="action-submenu"
            getRedistributeData={getRedistributeData}
            onRefreshReportList={() => {
              refetchGetDistributedDocs();
              setSelectedRowId(undefined);
            }}
            showToastFromParent={(config: {
              message?: string;
              type?: ToastType;
            }) => {
              if (config.message) {
                triggerToast(config.message, config.type ?? ToastType.Success);
              }
            }}
          />
          <ArrButton
            variant="secondary"
            size="small"
            dataTestId="add-reviewers-button-secondary"
            startIcon={<PlusIcon />}
            aria-label={t('assignment.manageReviewers.addreviewers')}
            className="add-reviewer-btn"
            onClick={() => {
              setIsAddRevierwerMenuOpen(true);
            }}
            id="add-reviewer-button"
            name="add-reviewer-button"
          >
            {t('assignment.manageReviewers.addreviewers')}
          </ArrButton>
        </Box>
      </Box>
      <ArrDataGridPro
        rows={
          distributeDocData?.assignedUsers
            ?.filter((item) => item.reviewerId != null)
            ?.map((item) => ({
              ...item,
              id: item.reviewerId,
            })) ?? []
        }
        columns={manage_reviewers(renderActionsCell)}
        primaryKey="id"
        loading={isDocViewLoading || isDocListLoading}
      />
      {isAddRevierwerMenuOpen && (
        <ArrDrawer
          showButton={false}
          showHeader={false}
          isOpen={isAddRevierwerMenuOpen}
          size="large"
          side="right"
          onClose={handleOnCloseDrawer}
        >
          {isLoadingGetUnAssignedUsers || isLoadingAddAssignedReviewers ? (
            <ArrLoadingSpinner />
          ) : (
            contentBody
          )}
        </ArrDrawer>
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={selectedReviewer?.assigned ?? 0}
        show={showDocLimitDialog}
        destination={docLimitSourceTypeCode}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />
      <ArrModal
        showModal={showModal}
        title={t('common.areYouSure')}
        description={modalTitle}
        dataTestId="managereviewers-confirm-modal"
        onClose={() => setShowModal(false)}
        autoFocusCloseButton={true}
        showClose={false}
        buttons={[
          {
            id: 'no-button',
            label: t('common.no'),
            dataTestId: 'mc-no-button',
            onClick: () => setShowModal(false),
            variant: 'secondary',
          },
          {
            id: 'yes-button',
            label: t('common.yes'),
            dataTestId: 'mc-yes-button',
            onClick: handleYesClick,
            variant: 'primary',
            loading: checkLoading(),
          },
        ]}
      />
      {/* Bulk Folder Flyout */}
      {isFolderDocsAction && (
        <BulkFolderUnFolder
          isOpen={isFolderDocsAction}
          setOpenActionMenu={setIsFolderDocsAction}
          handleBulkFolderContinue={handleBulkFolderContinue}
          isNonbg={true}
          masterDocListId={masterDocListId}
          setSelectedNewExistingFolder={setSelectedNewExistingFolder}
          nonBgStandardCount={totalDocsCount}
        />
      )}
      {/* Loading Spinner for Folder Docs Actions */}
      {(isBulkActionsPopupLoading ||
        isLoadingBulkActionsLikePopup ||
        isDoActionLoading) && <ArrLoadingSpinner overlay isOpen />}
      {/* Action Like Documents Modal */}
      {isActionLikeModal && isActionLikeModal !== 'BulkTagReviewer' && (
        <ActionLikeDocuments
          title={t('docExplorer.actionMenu.actionLikeDocuments.title')}
          showModal={!!isActionLikeModal}
          onClose={handleActionLikeClose}
          onCancel={handleActionLikeCancel}
          onContinue={handleActionLikeContinue}
          actionList={initialActionList}
          onActionChange={onActionLikeChange}
          continueButtonLabel={t('assignment.manageReviewers.finalizeFolder')}
          masterDocListId={masterDocListId}
          actionType="FOLDER"
          analyticsIndicatorStatus={analyticsIndicatorStatus}
          isNonBg={true}
          nonBgAssignmentId={assignmentId ?? 0}
          nonBgReviewerId={selectedReviewer?.reviewerId ?? 0}
          nonBgDocCount={totalDocsCount}
          dataTestId="action-like-documents"
        />
      )}
      <BulkTagUntag
        isOpen={isBulkTagAction}
        setOpenActionMenu={setIsBulkTagAction}
        handleBulkTagContinue={handleBulkTagContinue}
        masterDocListId={0}
        setSelectedNewExistingTag={setSelectedNewExistingTag}
        isNonbg={true}
        nonBgStandardCount={nonBgStandardCount}
        isManageReviewers={true}
      />
      {isActionLikeModal && isActionLikeModal === 'BulkTagReviewer' && (
        <ActionLikeDocuments
          dataTestId="action-like"
          showModal={!!isActionLikeModal}
          onClose={() => {
            setIsActionLikeModal(false);
            setActionLikeChangeList(undefined);
            setAnalyticsIndicatorStatus(0);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
          }}
          actionList={actionList}
          onCancel={() => {
            setIsActionLikeModal(false);
            setActionLikeChangeList(undefined);
            setAnalyticsIndicatorStatus(0);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
          }}
          onContinue={handleActionLike}
          continueButtonLabel={'Finalize Tag'}
          onActionChange={onActionLikeChangeList}
          masterDocListId={0}
          analyticsIndicatorStatus={analyticsIndicatorStatus}
          actionType={'TAG'}
          isNonBg={true}
          nonBgAssignmentId={assignmentId ?? 0}
          nonBgReviewerId={selectedReviewer?.reviewerId ?? 0}
          nonBgDocCount={nonBgStandardCount}
        />
      )}
      {isBulkWarning && (
        <BulkTaggingWarning
          dataTestId="bulk-tag-warning"
          onClose={() => {
            setIsBulkWarning(false);
            setActionLikeChangeList(undefined);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
          }}
          showModal={isBulkWarning}
          sourceType={SourceTypeCode.DOCEXPLORER}
          actionLikeChangeList={actionLikeChangeList}
          masterDocListId={Number(masterDocListId)}
          selectedNewExistingTag={getSelectedNewExistingTag()}
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setIsBulkWarning(false);
                setActionLikeChangeList(undefined);
                if (selectedNewExistingTag?.type === 'new') {
                  setIsBulkTagAction(false);
                }
              },
              variant: 'secondary',
              dataTestId: 'mr-cancel-btn',
            },
            {
              id: '2',
              label: t('common.ok'),
              onClick: handleBulkWarning,
              variant: 'primary',
              dataTestId: 'mr-ok-btn',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default ManageReviewers;
